import React from 'react'

import { SUPPORTED_BROWSERS, UNSUPPORTED_BROWSERS } from '../../utils/browserSupport'

import { Box } from '@/components/ui/base'
import { Button } from '@/components/ui/base'
import { Modal } from '@/components/ui/base'
import { SpaceBetween } from '@/components/ui/base'

interface BrowserSupportModalProps {
  visible: boolean
  onDismiss: () => void
}

export const BrowserSupportModal: React.FC<BrowserSupportModalProps> = ({ visible, onDismiss }) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Supported Browsers"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Got it
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Box>
          <h3>Why Browser Support Matters</h3>
          <p>Free Pattern Warehouse uses modern web technologies to provide you with the best experience:</p>
          <ul>
            <li>
              <strong>WebP Images</strong> - Load 95% faster than traditional formats
            </li>
            <li>
              <strong>Modern CSS</strong> - Responsive layouts that work on all devices
            </li>
            <li>
              <strong>Advanced Features</strong> - Drag-and-drop pattern editing, real-time previews
            </li>
          </ul>
          <p>Using an outdated browser may result in broken images, layout issues, or missing functionality.</p>
        </Box>

        <Box>
          <h3>Supported Browsers</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
            }}
          >
            {SUPPORTED_BROWSERS.map((browser) => (
              <div
                key={browser.name}
                style={{
                  padding: '12px',
                  border: '1px solid #e1e4e8',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <strong>{browser.name}</strong>
                <br />
                <span style={{ color: '#666', fontSize: '0.9em' }}>Version {browser.minVersion}+</span>
              </div>
            ))}
          </div>
        </Box>

        <Box>
          <h3>Unsupported Browsers</h3>
          <SpaceBetween size="xs">
            {UNSUPPORTED_BROWSERS.map((browser, index) => (
              <div
                key={index}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '4px',
                }}
              >
                <strong>
                  {browser.name} {browser.version || ''}
                </strong>
                <br />
                <span style={{ color: '#666', fontSize: '0.9em' }}>{browser.reason}</span>
              </div>
            ))}
          </SpaceBetween>
        </Box>

        <Box>
          <h3>Need to Update?</h3>
          <p>Download the latest version of your preferred browser:</p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a
              href="https://www.google.com/chrome/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0073bb', textDecoration: 'none' }}
            >
              Download Chrome →
            </a>
            <a
              href="https://www.mozilla.org/firefox/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0073bb', textDecoration: 'none' }}
            >
              Download Firefox →
            </a>
            <a
              href="https://www.apple.com/safari/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0073bb', textDecoration: 'none' }}
            >
              Update Safari →
            </a>
            <a
              href="https://www.microsoft.com/edge"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0073bb', textDecoration: 'none' }}
            >
              Download Edge →
            </a>
          </div>
        </Box>
      </SpaceBetween>
    </Modal>
  )
}

export default BrowserSupportModal
